import type { ReminderRule, Expense } from './db'

export type ReminderStatus = 'overdue' | 'warning' | 'ok'

export interface ReminderState {
  rule: ReminderRule
  status: ReminderStatus
  remainingKm: number | null
  remainingDays: number | null
  progressKm: number | null   // 0–1，null = 無里程週期
  progressDays: number | null // 0–1，null = 無月數週期
  dueDate: string | null      // 'YYYY-MM-DD'
  dueKm: number | null
}

function addMonths(dateStr: string, months: number): string {
  const d = new Date(dateStr)
  d.setMonth(d.getMonth() + months)
  return d.toISOString().slice(0, 10)
}

function daysBetween(a: string, b: string): number {
  return Math.round((new Date(b).getTime() - new Date(a).getTime()) / 86400000)
}

/** 計算單一 rule 的狀態 */
export function calcReminderState(
  rule: ReminderRule,
  currentKm: number,
  today: string,
  /** 標記過此 rule 的最新 expense（由呼叫方傳入） */
  lastExpense: Expense | null,
): ReminderState {
  // 起始點
  const startDate: string | null =
    lastExpense?.date ?? rule.last_done_date ?? null
  const startKm: number | null =
    lastExpense?.odometer_km ?? rule.last_done_km ?? null

  // 里程狀態
  let remainingKm: number | null = null
  let dueKm: number | null = null
  let progressKm: number | null = null

  if (rule.interval_km != null && startKm != null) {
    dueKm = startKm + rule.interval_km
    remainingKm = dueKm - currentKm
    progressKm = Math.min(1, (currentKm - startKm) / rule.interval_km)
  }

  // 日期狀態
  let remainingDays: number | null = null
  let dueDate: string | null = null
  let progressDays: number | null = null

  if (rule.interval_months != null && startDate != null) {
    dueDate = addMonths(startDate, rule.interval_months)
    remainingDays = daysBetween(today, dueDate)
    const totalDays = daysBetween(startDate, dueDate)
    progressDays = totalDays > 0
      ? Math.min(1, daysBetween(startDate, today) / totalDays)
      : 1
  }

  // 判斷狀態：逾期 > 警告 > 正常
  let status: ReminderStatus = 'ok'

  const kmOverdue  = remainingKm  != null && remainingKm  <= 0
  const dayOverdue = remainingDays != null && remainingDays <= 0

  if (kmOverdue || dayOverdue) {
    status = 'overdue'
  } else {
    const kmWarn  = remainingKm  != null && remainingKm  <= rule.warn_before_km
    const dayWarn = remainingDays != null && remainingDays <= rule.warn_before_days
    if (kmWarn || dayWarn) status = 'warning'
  }

  return { rule, status, remainingKm, remainingDays, progressKm, progressDays, dueDate, dueKm }
}

/** 批次計算所有 rules，回傳依嚴重程度排序的清單 */
export function calcAllReminders(
  rules: ReminderRule[],
  expenses: Expense[],
  currentKm: number,
  today: string,
): ReminderState[] {
  const ORDER: Record<ReminderStatus, number> = { overdue: 0, warning: 1, ok: 2 }

  return rules
    .filter(r => r.enabled)
    .map(rule => {
      const last = expenses
        .filter(e => e.reminder_rule_id === rule.id && e.deleted_at === null)
        .sort((a, b) => b.date.localeCompare(a.date))[0] ?? null
      return calcReminderState(rule, currentKm, today, last)
    })
    .sort((a, b) => ORDER[a.status] - ORDER[b.status])
}
